import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { Button, CommandGroup, CommandItem } from '@scio3/ui';
import { EnterHint } from './EnterHint';
import { useVisibleNavTargets } from '../hooks/useVisibleNavTargets';
import { useSearchRecentsStore } from '../stores/search-recents-store';
import { ENTITY_ICONS, getItemName, type SearchEntityType } from '../utils/entity-types';
import type { NavTarget } from '../utils/nav-targets';

interface RecentSearchGroupProps {
  /** Replay an entity row through the shared select dispatcher. */
  onSelectEntity: (entityType: string, item: Record<string, unknown>) => void;
  /** Follow a "Go to" quick-nav row. */
  onSelectNav: (route: string) => void;
}

/** Renderable row — a resolved nav target, or an entity to replay. */
type RecentRow =
  | { kind: 'nav'; key: string; target: NavTarget }
  | { kind: 'entity'; key: string; entityType: string; item: Record<string, unknown> };

/**
 * "Recent" section of the global-search dropdown, shown while the input is
 * empty — the panel used to open as a large blank box. Rows are whatever the
 * user last opened FROM search (see `search-recents-store`).
 *
 * Row shape mirrors the live result rows (leading icon slot + truncating
 * single-line label) so the panel reads as one continuous list.
 */
export function RecentSearchGroup({ onSelectEntity, onSelectNav }: RecentSearchGroupProps) {
  const { t } = useTranslation();
  const items = useSearchRecentsStore((s) => s.items);
  const clear = useSearchRecentsStore((s) => s.clear);
  // Nav rows persist only the target id, so a permission / plan / branding
  // change since the row was recorded drops it here rather than rendering a
  // link the user can no longer follow.
  const visibleNavTargets = useVisibleNavTargets();

  const rows: RecentRow[] = items
    .map((entry): RecentRow | null => {
      if (entry.kind === 'nav') {
        const target = visibleNavTargets.find((navTarget) => navTarget.id === entry.navTargetId);
        return target ? { kind: 'nav', key: entry.key, target } : null;
      }
      return { kind: 'entity', key: entry.key, entityType: entry.entityType, item: entry.item };
    })
    .filter((row): row is RecentRow => row !== null);

  if (rows.length === 0) return null;

  return (
    <CommandGroup
      heading={
        <div className="flex items-center justify-between gap-2">
          <span>{t('Recently visited')}</span>
          <Button
            variant="ghost"
            size="sm"
            className="-my-1 h-auto px-1 py-0 text-xs font-normal text-muted-foreground hover:bg-transparent hover:text-foreground"
            data-testid="global-search-recents-clear"
            onClick={clear}
          >
            {t('Clear')}
          </Button>
        </div>
      }
    >
      {rows.map((row) => {
        if (row.kind === 'nav') {
          return (
            <CommandItem
              key={row.key}
              value={`recent-${row.key}`}
              data-testid={`global-search-recent-${row.key}`}
              onSelect={() => onSelectNav(row.target.route)}
              className="group min-h-9"
            >
              {/* Same shape as the "Go to" rows below: a 4x4 icon slot holding
                  a smaller arrow, so both label columns line up. */}
              <span
                className="mr-2 flex h-4 w-4 shrink-0 items-center justify-center"
                aria-hidden="true"
              >
                <ArrowRight className="!h-3.5 !w-3.5 text-muted-foreground" />
              </span>
              <div className="flex min-w-0 flex-col">
                <span className="truncate">
                  {row.target.crumbs.map((crumb) => t(crumb)).join(' › ')}
                </span>
              </div>
              <EnterHint />
            </CommandItem>
          );
        }

        const Icon = ENTITY_ICONS[row.entityType as SearchEntityType];
        return (
          <CommandItem
            key={row.key}
            value={`recent-${row.key}`}
            data-testid={`global-search-recent-${row.key}`}
            onSelect={() => onSelectEntity(row.entityType, row.item)}
            className="group min-h-9"
          >
            {Icon && <Icon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />}
            <div className="flex min-w-0 flex-col">
              <span className="truncate">{getItemName(row.entityType, row.item, t)}</span>
            </div>
            <EnterHint />
          </CommandItem>
        );
      })}
    </CommandGroup>
  );
}
